import { useState, useEffect, useRef, useMemo } from "react";
import "./styles/callScreen.css";
import {
  PhoneOff,
  User,
  Mail,
  Calendar,
  Phone,
  MapPin,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
  Check,
  RotateCcw,
  AlertCircle,
  Loader,
  Minus,
} from "lucide-react";
import { humanReadableDate } from "../../../helper/dateTimeHelper";
import api from "../../../services/api";
import { useDispatch } from "react-redux";
import { callActions } from "../../../core/data/redux/live-call/slice";
import type { AppDispatch } from "../../../core/data/redux/store";
import { getWhatsAppNumber } from "./helpers/whatsapp.helper";

interface CallScreenProps {
  record: any;
  statusCall: boolean;
  isAutoCall: boolean;
  onEndCall: () => void;
  onAutoCallToggle: (stopped: boolean) => void;
  onAddNote?: (note?: string) => void;
  onSaveEdit?: (updated: Record<string, any>) => void;
}

const FilledPlayIcon = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5,3 19,12 5,21" />
  </svg>
);

const FilledSquareIcon = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <rect x="3" y="3" width="18" height="18" rx="2" />
  </svg>
);

const isMobileField = (label: string) => /Mobile No/i.test(label);
const isNameField = (label: string) =>
  /^(full\s?name|name|student\s?name|lead\s?name)$/i.test(label.trim());

const CallScreen = ({
  record,
  statusCall,
  isAutoCall,
  onEndCall,
  onAutoCallToggle,
  onAddNote,
  onSaveEdit,
}: CallScreenProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const signature = localStorage.getItem("signature");

  const [seconds, setSeconds] = useState(0);
  const [isEnding, setIsEnding] = useState(false);
  const [autoCallStopped, setAutoCallStopped] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const positionRef = useRef(position);
  const rafRef = useRef<number | null>(null);
  const callScreenRef = useRef<HTMLDivElement>(null);
  const [localRecord, setLocalRecord] = useState<any>(record);

  const minRef = useRef<HTMLDivElement>(null);
  const [minPosition, setMinPosition] = useState({ x: 0, y: 0 });
  const minPositionRef = useRef(minPosition);
  const minRafRef = useRef<number | null>(null);
  const [isMinDragging, setIsMinDragging] = useState(false);
  const [minDragStart, setMinDragStart] = useState({ x: 0, y: 0 });
  const minWasDraggedRef = useRef(false);

  const [logsExpanded, setLogsExpanded] = useState(true);
  const [infoExpanded, setInfoExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState("");

  const noteTextRef = useRef(noteText);
  useEffect(() => {
    noteTextRef.current = noteText;
  }, [noteText]);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formResponses, setFormResponses] = useState<Record<string, string>>(
    {},
  );

  const [countries, setCountries] = useState<any[]>([]);
  const [statesAll, setStatesAll] = useState<any[]>([]);
  const [districtAll, setDistrictAll] = useState<any[]>([]);
  const [citiesAll, setCitiesAll] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [addressLoaded, setAddressLoaded] = useState(false);

  const callSource = useMemo(
    () =>
      localRecord?.pipeline_char || localRecord?.mobile_no ? "leads" : "live",
    [localRecord],
  );

  const formStructure: any[] = useMemo(
    () => localRecord?.form?.structure || [],
    [localRecord?.form],
  );

  const dynamicFormFields = useMemo(
    () =>
      formStructure.filter(
        (f: any) =>
          !isMobileField(f.label) &&
          !isNameField(f.label) &&
          f.label !== "Email",
      ),
    [formStructure],
  );

  const getDisplayName = () =>
    localRecord?.name || localRecord?.response?.["Full Name"] || "Unknown";

  const getMobileNumber = () =>
    localRecord?.mobileNumber || localRecord?.mobile_no || "N/A";

  const getLeadDetails = () => ({
    email: localRecord?.email,
    country: localRecord?.country,
    city: localRecord?.city,
    state: localRecord?.state,
    district: localRecord?.district,
    pipeline: localRecord?.pipeline_char,
    assignedTo: localRecord?.assigned_to_name,
    leadSource: localRecord?.lead_source,
    leadSourceChild: localRecord?.lead_source_child,
    followUpDate: localRecord?.follow_up_date,
    updatedAt: localRecord?.updatedAt,
  });

  const buildFormResponses = (): Record<string, string> => {
    const resp = localRecord?.response || {};
    return Object.fromEntries(
      Object.entries(resp).map(([k, v]) => [k, String(v ?? "")]),
    );
  };

  useEffect(() => {
    setLocalRecord(record);
  }, [record?.id]);

  useEffect(() => {
    setSeconds(0);
    dispatch(callActions.setPendingNote(""));
    setFormResponses(buildFormResponses());
    setIsEditing(false);
    setNoteText("");
    setSaveError(null);
    setAddressLoaded(false);
    setSelectedCountry("");
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedCity("");
  }, [record?.id]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(callActions.setPendingNote(noteText));
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [noteText]);

  useEffect(() => {
    if (isEditing && callSource === "leads" && !addressLoaded)
      loadAddressData();
  }, [isEditing]);

  const loadAddressData = async () => {
    try {
      const [cRes, sRes, dRes, ciRes] = await Promise.all([
        api.post("userService/getCountry", { signature }),
        api.post("userService/getState", { signature }),
        api.post("userService/getDistrict", { signature }),
        api.post("userService/getCity", { signature }),
      ]);
      const allCountries = cRes.data.data || [];
      const allStates = sRes.data.data || [];
      const allDistricts = dRes.data.data || [];
      const allCities = ciRes.data.data || [];
      setCountries(allCountries);
      setStatesAll(allStates);
      setDistrictAll(allDistricts);
      setCitiesAll(allCities);
      const existingCountry = formResponses["Country"] || record?.country || "";
      if (existingCountry) {
        const countryObj = allCountries.find(
          (c: any) => c.name?.toLowerCase() === existingCountry.toLowerCase(),
        );
        if (countryObj) {
          setSelectedCountry(`${countryObj.name}-${countryObj.id}`);
          const filteredStates = allStates.filter(
            (s: any) => parseInt(s.country_id) === countryObj.id,
          );
          setStates(filteredStates);
          const existingState = formResponses["State"] || record?.state || "";
          if (existingState) {
            const stateObj = filteredStates.find(
              (s: any) => s.name?.toLowerCase() === existingState.toLowerCase(),
            );
            if (stateObj) {
              setSelectedState(`${stateObj.name}-${stateObj.id}`);
              const filteredDistricts = allDistricts.filter(
                (d: any) => parseInt(d.state_id) === stateObj.id,
              );
              const filteredCities = allCities.filter(
                (c: any) => parseInt(c.state_id) === stateObj.id,
              );
              setDistricts(filteredDistricts);
              setCities(filteredCities);
              const existingDistrict =
                formResponses["District"] || record?.district || "";
              if (existingDistrict) {
                const dObj = filteredDistricts.find(
                  (d: any) =>
                    d.district?.toLowerCase() ===
                    existingDistrict.toLowerCase(),
                );
                if (dObj) setSelectedDistrict(`${dObj.district}-${dObj.id}`);
              }
              const existingCity = formResponses["City"] || record?.city || "";
              if (existingCity) {
                const cObj = filteredCities.find(
                  (c: any) =>
                    c.name?.toLowerCase() === existingCity.toLowerCase(),
                );
                if (cObj) setSelectedCity(`${cObj.name}-${cObj.id}`);
              }
            }
          }
        } else {
          const india = allCountries.find(
            (c: any) => c.name?.toLowerCase() === "india",
          );
          if (india) {
            setSelectedCountry(`${india.name}-${india.id}`);
            setFormResponses((p) => ({ ...p, Country: india.name }));
            setStates(
              allStates.filter((s: any) => parseInt(s.country_id) === india.id),
            );
          }
        }
      }
      setAddressLoaded(true);
    } catch (e) {
      console.error("Failed to load address data", e);
    }
  };

  const onCountryChange = (val: string) => {
    const [name, id] = val.split("-");
    setSelectedCountry(val);
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedCity("");
    setFormResponses((p) => ({
      ...p,
      Country: name,
      State: "",
      District: "",
      City: "",
    }));
    setStates(statesAll.filter((s) => parseInt(s.country_id) === parseInt(id)));
    setDistricts([]);
    setCities([]);
  };
  const onStateChange = (val: string) => {
    const [name, id] = val.split("-");
    setSelectedState(val);
    setSelectedDistrict("");
    setSelectedCity("");
    setFormResponses((p) => ({ ...p, State: name, District: "", City: "" }));
    setDistricts(
      districtAll.filter((d) => parseInt(d.state_id) === parseInt(id)),
    );
    setCities(citiesAll.filter((c) => parseInt(c.state_id) === parseInt(id)));
  };
  const onDistrictChange = (val: string) => {
    setSelectedDistrict(val);
    setFormResponses((p) => ({ ...p, District: val.split("-")[0] }));
  };
  const onCityChange = (val: string) => {
    setSelectedCity(val);
    setFormResponses((p) => ({ ...p, City: val.split("-")[0] }));
  };

  const handleSaveEdit = async () => {
    if (isSaving) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      await api.post("leadService/editLeadResponse", {
        lead_id: record?.id,
        response: formResponses,
        signature,
      });
      setLocalRecord((prev: any) => ({
        ...prev,
        name: formResponses["Full Name"] ?? prev?.name,
        email: formResponses["Email"] ?? prev?.email,
        country: formResponses["Country"] ?? prev?.country,
        state: formResponses["State"] ?? prev?.state,
        district: formResponses["District"] ?? prev?.district,
        city: formResponses["City"] ?? prev?.city,
        response: { ...(prev?.response || {}), ...formResponses },
      }));
      onSaveEdit?.({ response: formResponses });
      setIsEditing(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message?.[0]?.msg ||
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        "Failed to save. Please try again.";
      setSaveError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setIsSaving(false);
    }
  };
  const handleCancelEdit = () => {
    setFormResponses(buildFormResponses());
    setSaveError(null);
    setIsEditing(false);
  };

  const renderFormField = (field: any) => {
    const { label, type, options } = field;
    const val = formResponses[label] ?? "";
    if (!isEditing) {
      if (type?.toLowerCase() === "country") {
        const cols = ["Country", "State", "District", "City"];
        const hasFilled = cols.some(
          (k) => formResponses[k] || (record as any)?.[k.toLowerCase()],
        );
        if (!hasFilled) return <span className="cs-info-value">—</span>;
        return (
          <div className="cs-country-view">
            {cols.map((sub) => {
              const v =
                formResponses[sub] ||
                (record as any)?.[sub.toLowerCase()] ||
                "";
              return v ? (
                <span key={sub} className="cs-country-view-chip">
                  <span className="cs-country-view-sub">{sub}</span>
                  {v}
                </span>
              ) : null;
            })}
          </div>
        );
      }
      return <span className="cs-info-value">{val || "—"}</span>;
    }
    if (type?.toLowerCase() === "country") {
      const cols = [
        {
          sub: "Country",
          list: countries,
          value: selectedCountry,
          getOpt: (x: any) => ({ val: `${x.name}-${x.id}`, lbl: x.name }),
          onChange: onCountryChange,
          disabled: false,
        },
        {
          sub: "State",
          list: states,
          value: selectedState,
          getOpt: (x: any) => ({ val: `${x.name}-${x.id}`, lbl: x.name }),
          onChange: onStateChange,
          disabled: states.length === 0,
        },
        {
          sub: "District",
          list: districts,
          value: selectedDistrict,
          getOpt: (x: any) => ({
            val: `${x.district}-${x.id}`,
            lbl: x.district,
          }),
          onChange: onDistrictChange,
          disabled: districts.length === 0,
        },
        {
          sub: "City",
          list: cities,
          value: selectedCity,
          getOpt: (x: any) => ({ val: `${x.name}-${x.id}`, lbl: x.name }),
          onChange: onCityChange,
          disabled: cities.length === 0,
        },
      ];
      return (
        <div className="cs-country-grid" onClick={(e) => e.stopPropagation()}>
          {cols.map(({ sub, list, value, getOpt, onChange, disabled }) => (
            <div key={sub} className="cs-country-col">
              <span className="cs-country-sublabel">{sub}</span>
              <select
                className="cs-info-select"
                value={value || ""}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}>
                <option value="">Select</option>
                {list.map((item: any) => {
                  const { val: v, lbl } = getOpt(item);
                  return (
                    <option key={item.id} value={v}>
                      {lbl}
                    </option>
                  );
                })}
              </select>
            </div>
          ))}
        </div>
      );
    }
    if (type === "date")
      return (
        <input
          className="cs-info-input"
          type="date"
          value={val}
          onChange={(e) =>
            setFormResponses((p) => ({ ...p, [label]: e.target.value }))
          }
          onClick={(e) => e.stopPropagation()}
        />
      );
    if (type === "select" && Array.isArray(options))
      return (
        <select
          className="cs-info-select"
          value={val}
          onChange={(e) =>
            setFormResponses((p) => ({ ...p, [label]: e.target.value }))
          }
          onClick={(e) => e.stopPropagation()}>
          <option value="">Select…</option>
          {options.map((opt: string) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    if (type === "textarea")
      return (
        <textarea
          className="cs-info-input cs-info-textarea"
          value={val}
          rows={2}
          placeholder={`Enter ${label}`}
          onChange={(e) =>
            setFormResponses((p) => ({ ...p, [label]: e.target.value }))
          }
          onClick={(e) => e.stopPropagation()}
        />
      );
    return (
      <input
        className="cs-info-input"
        value={val}
        placeholder={`Enter ${label}`}
        onChange={(e) =>
          setFormResponses((p) => ({ ...p, [label]: e.target.value }))
        }
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  const ro = (display: string) => (
    <span className="cs-info-value">{display || "—"}</span>
  );

  const recentLogs = useMemo(() => {
    try {
      const groups: any[] = Array.isArray(localRecord?.activities)
        ? localRecord.activities
        : [];
      return groups
        .flatMap((g: any) =>
          Array.isArray(g?.activities)
            ? g.activities.map((a: any) => ({ ...a, groupDate: g.date }))
            : [],
        )
        .slice(0, 2);
    } catch {
      return [];
    }
  }, [localRecord?.activities]);

  // Timer tick — starts/stops with statusCall
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (statusCall) timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [statusCall]);

  const formatTime = (sec: number) =>
    `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;

  const handleEndCall = () => {
    if (isEnding) return;
    setIsEnding(true);
    dispatch(callActions.setPendingNote(noteTextRef.current));
    if (onAddNote) onAddNote(noteTextRef.current);
    setTimeout(() => onEndCall(), 600);
  };
  const handleToggleMinimize = () => setIsMinimized((s) => !s);
  const handleToggleAutoCall = () => {
    const newVal = !autoCallStopped;
    setAutoCallStopped(newVal);
    onAutoCallToggle(newVal);
  };

  // ── Minimized pill drag ──
  const handleMinPointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest && target.closest("button, .min-end-btn")) return;
    if (!minRef.current) return;
    minRef.current.setPointerCapture?.(e.pointerId);
    minWasDraggedRef.current = false;
    setIsMinDragging(true);
    setMinDragStart({
      x: e.clientX - minPositionRef.current.x,
      y: e.clientY - minPositionRef.current.y,
    });
    minRef.current.style.transition = "none";
  };
  const handleMinPointerMove = (e: PointerEvent) => {
    if (!isMinDragging || !minRef.current) return;
    const rect = minRef.current.getBoundingClientRect();
    const clampedX = Math.max(
      0,
      Math.min(e.clientX - minDragStart.x, window.innerWidth - rect.width),
    );
    const clampedY = Math.max(
      0,
      Math.min(e.clientY - minDragStart.y, window.innerHeight - rect.height),
    );
    if (minRafRef.current !== null) cancelAnimationFrame(minRafRef.current);
    minRafRef.current = requestAnimationFrame(() => {
      if (!minRef.current) return;
      minRef.current.style.transform = `translate(${clampedX}px, ${clampedY}px)`;
    });
    minPositionRef.current = { x: clampedX, y: clampedY };
    minWasDraggedRef.current = true;
    e.preventDefault?.();
  };
  const handleMinPointerUp = () => {
    setIsMinDragging(false);
    setMinPosition(minPositionRef.current);
    if (minRef.current)
      minRef.current.style.transition = "box-shadow 0.2s ease";
    if (minRafRef.current !== null) {
      cancelAnimationFrame(minRafRef.current);
      minRafRef.current = null;
    }
  };
  useEffect(() => {
    if (isMinDragging) {
      window.addEventListener("pointermove", handleMinPointerMove);
      window.addEventListener("pointerup", handleMinPointerUp);
    }
    return () => {
      window.removeEventListener("pointermove", handleMinPointerMove);
      window.removeEventListener("pointerup", handleMinPointerUp);
    };
  }, [isMinDragging, minDragStart]);

  // ── Main card drag ──
  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest && target.closest("button, .control-btn, .cs-close-btn"))
      return;
    if (!callScreenRef.current) return;
    callScreenRef.current.setPointerCapture?.(e.pointerId);
    setIsDragging(true);
    setDragStart({
      x: e.clientX - positionRef.current.x,
      y: e.clientY - positionRef.current.y,
    });
    callScreenRef.current.style.transition = "none";
  };
  const handlePointerMove = (e: PointerEvent) => {
    if (!isDragging || !callScreenRef.current) return;
    const rect = callScreenRef.current.getBoundingClientRect();
    const clampedX = Math.max(
      0,
      Math.min(e.clientX - dragStart.x, window.innerWidth - rect.width),
    );
    const clampedY = Math.max(
      0,
      Math.min(e.clientY - dragStart.y, window.innerHeight - rect.height),
    );
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!callScreenRef.current) return;
      callScreenRef.current.style.transform = `translate(${clampedX}px, ${clampedY}px)`;
    });
    positionRef.current = { x: clampedX, y: clampedY };
    e.preventDefault?.();
  };
  const handlePointerUp = () => {
    setIsDragging(false);
    setPosition(positionRef.current);
    if (callScreenRef.current)
      callScreenRef.current.style.transition = "box-shadow 0.6s ease";
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    }
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, dragStart]);

  // ── Initial position: bottom-right corner ──
  useEffect(() => {
    const el = callScreenRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (positionRef.current.x !== 0 || positionRef.current.y !== 0) return;
      const rect = el.getBoundingClientRect();
      const initX = Math.max(0, window.innerWidth - rect.width - 20);
      const initY = Math.max(0, window.innerHeight - rect.height - 20);
      positionRef.current = { x: initX, y: initY };
      setPosition({ x: initX, y: initY });
      el.style.transform = `translate(${initX}px, ${initY}px)`;
    });
  }, []);

  useEffect(() => {
    if (isMinimized && minRef.current) {
      const rect = minRef.current.getBoundingClientRect();
      const centerX = Math.max(0, (window.innerWidth - rect.width) / 2);
      setMinPosition({ x: centerX, y: 0 });
      minPositionRef.current = { x: centerX, y: 0 };
      minRef.current.style.transform = `translate(${centerX}px, 0px)`;
    }
  }, [isMinimized]);

  const leadDetails = getLeadDetails();
  const displayName = getDisplayName();
  const mobileNumber = getMobileNumber();
  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((w: string) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    const raw = getMobileNumber();
    if (!raw || raw === "N/A") return;
    const phone = getWhatsAppNumber(raw);
    if (phone) window.open(`https://wa.me/${phone}`, "_blank");
  };

  return (
    <div className="call-overlay">
      {/* ── Minimized Pill ── */}
      {isMinimized && (
        <div
          ref={minRef}
          className="call-minimized"
          onClick={() => {
            if (minWasDraggedRef.current) {
              minWasDraggedRef.current = false;
              return;
            }
            handleToggleMinimize();
          }}
          onPointerDown={handleMinPointerDown}
          role="button"
          title="Restore call"
          style={{
            transform: `translate(${minPosition.x}px, ${minPosition.y}px)`,
            cursor: isMinDragging ? "grabbing" : "grab",
          }}>
          <div
            className={`min-avatar-wrap ${statusCall ? "connected" : "calling"}`}>
            {!statusCall && (
              <>
                <span className="min-ripple" />
                <span className="min-ripple" />
                <span className="min-ripple" />
              </>
            )}
            <div className="min-avatar-ring" />
            <div className="min-avatar">
              {displayName !== "Unknown" ? (
                <span>{getInitials(displayName)}</span>
              ) : (
                <User size={16} strokeWidth={1.5} />
              )}
            </div>
          </div>
          <div className="min-info">
            <div className="min-name">{displayName}</div>
            {mobileNumber && <div className="min-number">{mobileNumber}</div>}
          </div>
          <button
            className="min-end-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleEndCall();
            }}
            title="End Call">
            <PhoneOff size={14} />
          </button>
        </div>
      )}

      {/* ── Main Card ── */}
      {!isMinimized && (
        <div
          ref={callScreenRef}
          className={`call-screen ${isEnding ? "ending" : ""}`}
          style={{ transform: `translate(${position.x}px, ${position.y}px)` }}>
          {/* ── Header ── */}
          <div
            className="cs-header"
            onPointerDown={handlePointerDown}
            style={{ cursor: isDragging ? "grabbing" : "grab" }}>
            {/* Avatar Banner */}
            <div className="cs-header-banner">
              <div
                className={`cs-avatar-wrap ${statusCall ? "connected" : "calling"}`}>
                {!statusCall && (
                  <>
                    <span className="cs-ripple" />
                    <span className="cs-ripple" />
                    <span className="cs-ripple" />
                  </>
                )}
                <div className="cs-avatar-ring" />
                <div className="cs-avatar">
                  {displayName !== "Unknown" ? (
                    <span>{getInitials(displayName)}</span>
                  ) : (
                    <User size={20} strokeWidth={1.5} />
                  )}
                </div>
              </div>
            </div>

            {/* Name + Number */}
            <div className="cs-caller-meta">
              <div className="cs-caller-name-row">
                <h3 className="cs-name">{displayName}</h3>
                {leadDetails.pipeline && (
                  <span className="cs-pipeline-inline">
                    {leadDetails.pipeline}
                  </span>
                )}
              </div>
              <div className="cs-number-row">
                {statusCall && <span className="cs-live-dot" />}
                <Phone size={11} className="cs-number-icon" />
                <span className="cs-number">{mobileNumber}</span>
              </div>
            </div>

            {/* Duration + Actions */}
            <div className="cs-header-part-2">
              <div className="cs-header-actions">
                <div className="cs-header-duration">
                  {!statusCall ? (
                    <div className="cs-calling-div">
                      <span className="cs-calling-dot" />
                      Calling...
                    </div>
                  ) : (
                    <span className="cs-timer cs-timer--active">
                      {formatTime(seconds)}
                    </span>
                  )}
                </div>

                {/* ── WhatsApp Button ── */}
                <button
                  type="button"
                  className="whatsapp-icon"
                  onClick={handleWhatsApp}
                  title="Open in WhatsApp">
                  <i className="ti ti-brand-whatsapp" />
                </button>

                {isAutoCall && (
                  <button
                    className={`cs-auto-btn ${autoCallStopped ? "active" : ""}`}
                    onClick={handleToggleAutoCall}
                    title={
                      autoCallStopped ? "Resume Auto Call" : "Stop Auto Call"
                    }>
                    {autoCallStopped ? (
                      <FilledPlayIcon size={11} />
                    ) : (
                      <FilledSquareIcon size={11} />
                    )}
                  </button>
                )}
                <button
                  className={`cs-hangup-btn ${isEnding ? "ending" : ""}`}
                  onClick={handleEndCall}
                  disabled={isEnding}
                  title="End Call">
                  <PhoneOff size={14} />
                </button>
              </div>
            </div>

            {/* Minimize Button */}
            <button
              className="cs-close-btn"
              style={{ background: "#7F7DD3", border: "none" }}
              onClick={handleToggleMinimize}
              title="Minimize">
              <Minus size={16} color="white" />
            </button>
          </div>

          {/* ── Body ── */}
          <div className="cs-body">
            {/* ── Contact Details ── */}
            <div
              className="cs-sec"
              style={{ border: "1px solid #EEEFF3", borderRadius: "12px" }}>
              <div className="cs-sec-hdr" style={{ borderRadius: "12px" }}>
                <div className="cs-sec-hdr-l">
                  <div className="cs-sec-icon-wrap cs-sec-icon-wrap--red">
                    <User size={13} />
                  </div>
                  <span className="cs-sec-title">Contact Details</span>
                </div>
                <div className="cs-sec-hdr-r">
                  {callSource === "leads" && !isEditing && (
                    <button
                      className="cs-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditing(true);
                      }}>
                      <i className="ti ti-pencil" style={{ fontSize: 11 }} />{" "}
                      Edit Details
                    </button>
                  )}
                  {callSource === "leads" && isEditing && (
                    <>
                      <button
                        className="cs-save-btn"
                        onClick={handleSaveEdit}
                        disabled={isSaving}>
                        {isSaving ? (
                          <>
                            <Loader size={11} className="cs-spin" /> Saving…
                          </>
                        ) : (
                          <>
                            <Check size={11} /> Save
                          </>
                        )}
                      </button>
                      <button
                        className="cs-cancel-btn"
                        onClick={handleCancelEdit}
                        disabled={isSaving}>
                        <RotateCcw size={11} /> Cancel
                      </button>
                    </>
                  )}
                  <button
                    className="cs-collapse-btn"
                    onClick={() => setInfoExpanded((v) => !v)}>
                    {infoExpanded ? (
                      <ChevronUp size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                  </button>
                </div>
              </div>

              {saveError && isEditing && (
                <div className="cs-save-error">
                  <AlertCircle size={12} />
                  <span>{saveError}</span>
                  <button
                    className="cs-save-error-dismiss"
                    onClick={() => setSaveError(null)}>
                    ×
                  </button>
                </div>
              )}

              <div
                className={`cs-sec-body ${infoExpanded ? "" : "cs-collapsed"}`}
                style={{ borderRadius: "12px" }}>
                <div className="cs-info-scroll-wrap">
                  <div className="cs-info-grid">
                    <div
                      className={`cs-info-cell ${isEditing ? "cs-info-cell--editing" : ""}`}>
                      <span className="cs-info-label">
                        <User size={10} /> Name
                      </span>
                      {callSource === "leads" && isEditing ? (
                        <input
                          className="cs-info-input"
                          value={formResponses["Full Name"] ?? displayName}
                          onChange={(e) =>
                            setFormResponses((p) => ({
                              ...p,
                              "Full Name": e.target.value,
                            }))
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        ro(displayName)
                      )}
                    </div>

                    <div className="cs-info-cell cs-info-cell--readonly">
                      <span className="cs-info-label">
                        <Phone size={10} /> Mobile No
                      </span>
                      {ro(mobileNumber)}
                    </div>

                    {(leadDetails.email || isEditing) && (
                      <div
                        className={`cs-info-cell ${isEditing ? "cs-info-cell--editing" : ""}`}>
                        <span className="cs-info-label">
                          <Mail size={10} /> Email ID
                        </span>
                        {callSource === "leads" && isEditing ? (
                          <input
                            className="cs-info-input"
                            value={
                              formResponses["Email"] ??
                              (leadDetails.email || "")
                            }
                            onChange={(e) =>
                              setFormResponses((p) => ({
                                ...p,
                                Email: e.target.value,
                              }))
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          ro(leadDetails.email || "")
                        )}
                      </div>
                    )}

                    {callSource === "live" && (
                      <>
                        {leadDetails.country && (
                          <div className="cs-info-cell">
                            <span className="cs-info-label">
                              <MapPin size={10} /> Country
                            </span>
                            {ro(leadDetails.country)}
                          </div>
                        )}
                        {leadDetails.state && (
                          <div className="cs-info-cell">
                            <span className="cs-info-label">
                              <MapPin size={10} /> State
                            </span>
                            {ro(leadDetails.state)}
                          </div>
                        )}
                        {leadDetails.district && (
                          <div className="cs-info-cell">
                            <span className="cs-info-label">
                              <MapPin size={10} /> District
                            </span>
                            {ro(leadDetails.district)}
                          </div>
                        )}
                        {leadDetails.city && (
                          <div className="cs-info-cell">
                            <span className="cs-info-label">
                              <MapPin size={10} /> City
                            </span>
                            {ro(leadDetails.city)}
                          </div>
                        )}
                      </>
                    )}

                    {callSource === "leads" && (
                      <>
                        {leadDetails.leadSource && (
                          <div className="cs-info-cell cs-info-cell--readonly">
                            <span className="cs-info-label">
                              <i className="ti ti-antenna" /> Source
                            </span>
                            <span className="cs-info-value">
                              {leadDetails.leadSource}
                              {leadDetails.leadSourceChild
                                ? ` / ${leadDetails.leadSourceChild}`
                                : ""}
                            </span>
                          </div>
                        )}
                        {leadDetails.assignedTo && (
                          <div className="cs-info-cell cs-info-cell--readonly">
                            <span className="cs-info-label">
                              <User size={10} /> Assigned To
                            </span>
                            {ro(leadDetails.assignedTo)}
                          </div>
                        )}
                        <div className="cs-info-cell cs-info-cell--readonly">
                          <span className="cs-info-label">
                            <Calendar size={10} /> Follow Up
                          </span>
                          <span className="cs-info-value">
                            {leadDetails.followUpDate
                              ? humanReadableDate(
                                  leadDetails.followUpDate,
                                  true,
                                )
                              : "—"}
                          </span>
                        </div>
                        {leadDetails.updatedAt && (
                          <div className="cs-info-cell cs-info-cell--readonly">
                            <span className="cs-info-label">
                              <Clock size={10} /> Last Modified
                            </span>
                            {ro(humanReadableDate(leadDetails.updatedAt, true))}
                          </div>
                        )}
                        {dynamicFormFields.map((field: any, index: number) => {
                          const isCountry =
                            field.type?.toLowerCase() === "country";
                          return (
                            <div
                              key={field.id ?? index}
                              className={[
                                "cs-info-cell",
                                isEditing ? "cs-info-cell--editing" : "",
                                isCountry ? "cs-info-cell--full" : "",
                              ]
                                .join(" ")
                                .trim()}>
                              {!isCountry && (
                                <span className="cs-info-label">
                                  <i className="ti ti-info-circle" />
                                  {field.label}
                                </span>
                              )}
                              {renderFormField(field)}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Recent Logs ── */}
            <div
              className="cs-sec cs-sec--logs"
              style={{ border: "1px solid #EEEFF3", borderRadius: "12px" }}>
              <div
                className="cs-sec-hdr cs-sec-hdr--clickable"
                onClick={() => setLogsExpanded((v) => !v)}
                style={{ borderRadius: "12px" }}>
                <div className="cs-sec-hdr-l">
                  <div className="cs-sec-icon-wrap cs-sec-icon-wrap--orange">
                    <i className="ti ti-history" style={{ fontSize: 13 }} />
                  </div>
                  <span className="cs-sec-title">Recent Logs</span>
                  {recentLogs.length > 0 ? (
                    <span className="cs-badge cs-badge--outline">
                      {String(recentLogs.length).padStart(2, "0")}
                    </span>
                  ) : (
                    <span className="cs-badge cs-badge--muted">No logs</span>
                  )}
                </div>
                <button
                  className="cs-collapse-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLogsExpanded((v) => !v);
                  }}>
                  {logsExpanded ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>
              </div>
              <div
                className={`cs-sec-body ${logsExpanded ? "" : "cs-collapsed"}`}>
                {recentLogs.length === 0 ? (
                  <div className="cs-no-logs">
                    <i className="ti ti-phone-off" />
                    <span>No call logs recorded yet</span>
                  </div>
                ) : (
                  <div className="cs-logs-grid">
                    {recentLogs.map((log: any, i: number) => {
                      const safeTitle = log?.title || log?.type || "Call";
                      const safeTime = log?.time || log?.date || null;
                      const safeDesc = log?.description || log?.note || null;
                      const safeRemind = log?.reminderDate || null;
                      return (
                        <div key={log?.id ?? i} className="cs-log-item">
                          <div className="cs-log-icon-wrap">
                            <i
                              className={`ti ${log?.icon || "ti-phone-call"}`}
                            />
                          </div>
                          <div className="cs-log-content">
                            <div className="cs-log-top">
                              <span className="cs-log-title">{safeTitle}</span>
                              {safeTime && (
                                <span className="cs-log-time">
                                  {humanReadableDate(
                                    safeTime,
                                    true,
                                    undefined,
                                    undefined,
                                    false,
                                  )}
                                </span>
                              )}
                            </div>
                            {safeDesc && (
                              <p className="cs-log-desc">{safeDesc}</p>
                            )}
                            {safeRemind && (
                              <span className="cs-log-reminder">
                                <i className="ti ti-bell" />
                                Reminder · {humanReadableDate(safeRemind, true)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── Call Notes ── */}
            <div
              className="cs-notes-section"
              style={{ border: "1px solid #EEEFF3", borderRadius: "12px" }}>
              <div className="cs-notes-header">
                <div className="cs-sec-icon-wrap cs-sec-icon-wrap--red">
                  <FileText size={13} />
                </div>
                <span className="cs-notes-title">Call Notes</span>
              </div>
              <textarea
                className="cs-notes-textarea"
                placeholder="Type your notes about this call"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <div className="cs-ended-overlay">
            <span>CALL ENDED</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallScreen;
